// ================================================================
// 貨幣選擇器組件
// ================================================================
// 版本：1.0
// 日期：2026-01-21
// 功能：允許用戶選擇偏好的顯示貨幣
// ================================================================

import React from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import { useCurrency } from '../../contexts/CurrencyContext';
import { Currency } from '../../api/currency';

// ================================================================
// Props 類型
// ================================================================

interface CurrencySelectorProps {
  /** 是否只顯示國旗圖標（簡潔模式） */
  compact?: boolean;
  /** 是否顯示貨幣名稱 */
  showName?: boolean;
  /** 自訂 sx 樣式 */
  sx?: object;
}

// ================================================================
// 貨幣選擇器組件
// ================================================================

const CurrencySelector: React.FC<CurrencySelectorProps> = ({
  compact = false,
  showName = false,
  sx = {},
}) => {
  const { preferredCurrency, currencies, setCurrency, isLoading } = useCurrency();

  const handleChange = (event: SelectChangeEvent<string>) => {
    setCurrency(event.target.value);
  };

  if (isLoading) {
    return null;
  }

  // 簡潔模式：只顯示國旗
  if (compact) {
    const currentCurrency = currencies.find((c: Currency) => c.code === preferredCurrency);

    return (
      <Tooltip title={`切換貨幣 (${currentCurrency?.name || 'NTD'})`}>
        <FormControl size="small" sx={{ minWidth: 60, ...sx }}>
          <Select
            value={preferredCurrency}
            onChange={handleChange}
            variant="outlined"
            renderValue={(value) => {
              const selected = currencies.find((c: Currency) => c.code === value);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box component="span" sx={{ fontSize: '1rem' }}>{selected?.flag}</Box>
                  <Typography variant="body2" sx={{ color: 'white' }}>{value}</Typography>
                </Box>
              );
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 1,
                  maxHeight: 300,
                },
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
            sx={{
              '.MuiSelect-select': {
                py: 0.5,
                px: 1,
                display: 'flex',
                alignItems: 'center',
              },
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.3)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.5)',
              },
            }}
          >
            {currencies.map((currency: Currency) => (
              <MenuItem key={currency.code} value={currency.code}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box component="span" sx={{ fontSize: '1.2rem' }}>{currency.flag}</Box>
                  <Typography variant="body2">{currency.code} - {currency.name}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Tooltip>
    );
  }

  // 完整模式
  return (
    <FormControl size="small" sx={{ minWidth: 120, ...sx }}>
      <Select
        value={preferredCurrency}
        onChange={handleChange}
        variant="outlined"
      >
        {currencies.map((currency: Currency) => (
          <MenuItem key={currency.code} value={currency.code}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" sx={{ fontSize: '1.2rem' }}>{currency.flag}</Box>
              <Typography variant="body2">
                {showName ? `${currency.name} (${currency.code})` : currency.code}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default CurrencySelector;
